/*
 * Copyright (c) 2021. Dave van Rijn Development
 */
import './style/timePicker.scss';

import React from 'react';
import {PureComponent} from 'react';
import classNames from "classnames";
import {DateType} from "../dateFieldController";
import {pad, remToPx} from "../../../util/controlUtil";
import {DateNumParts} from "../dateField";
import {colorIsWhite, convertColor} from "../../../util/colorUtil";
import {ControlContext} from "../../../util/controlContext";

interface Props {
    pickerValue: DateNumParts;
    dateType: DateType;
    onChange: (hour: number, minute: number) => void;
}

const SCROLL_TIMEOUT = 120;

export default class TimePicker extends PureComponent<Props> {
    declare context: React.ContextType<typeof ControlContext>;
    static contextType = ControlContext;

    private hourTimeout: number | null = null;
    private minuteTimeout: number | null = null;

    onScroll = (type: 'hour' | 'minute') => () => {
        const handle: number | null = type === 'hour' ? this.hourTimeout : this.minuteTimeout;
        this.clearTimeout(handle);
        if (type === 'hour') this.hourTimeout = window.setTimeout(this.determineTime, SCROLL_TIMEOUT);
        else this.minuteTimeout = window.setTimeout(this.determineTime, SCROLL_TIMEOUT);
    };

    getBaseColor = (allowWhite: boolean = true): string => {
        let color = convertColor(this.context.baseColor);
        if (!allowWhite && colorIsWhite(color)) color = convertColor(this.context.contrastColor);
        return color;
    };

    getContrastColor = (allowWhiteBase: boolean = true): string => {
        if (!allowWhiteBase) {
            const base = this.getBaseColor(true);
            if (colorIsWhite(base)) return base;
        }
        return convertColor(this.context.contrastColor);
    };

    clearTimeout = (handle: number | null) => {
        if (handle) window.clearTimeout(handle);
    };

    determineTime = () => {
        this.clearTimeout(this.minuteTimeout);
        this.clearTimeout(this.hourTimeout);

        const {pickerValue, onChange} = this.props, hourContainer = document.getElementById('hour-container'),
            minuteContainer = document.getElementById('minute-container');

        let hour = pickerValue.hour, minute = pickerValue.minute;
        if (hourContainer)
            hour = Math.floor(hourContainer.scrollTop / remToPx(2));

        if (minuteContainer)
            minute = Math.floor(minuteContainer.scrollTop / remToPx(2));

        onChange(hour, minute);
    };

    renderHourSelect = () => {
        const hours: number[] = [];
        for (let i = 0; i < 24; i++)
            hours.push(i);
        return (
            <div className='time-select-background-container'>
                <div className='selected-background'/>
                <div id='hour-container' className='time-select-container' onScroll={this.onScroll('hour')}>
                    <div className='time-unit-container invis'>
                        <label className='time-unit-label'>0</label>
                    </div>
                    {hours.map((hour: number, index: number) => (
                        <div id={`hour-${pad(hour)}`} key={index} className='time-unit-container'>
                            <label className='time-unit-label'>{pad(hour)}</label>
                        </div>
                    ))}
                    <div className='time-unit-container invis'>
                        <label className='time-unit-label'>0</label>
                    </div>
                </div>
            </div>
        )
    };

    renderMinuteSelect = () => {
        const minutes: number[] = [];
        for (let i = 0; i < 60; i++)
            minutes.push(i);
        return (
            <div className='time-select-background-container'>
                <div className='selected-background'/>
                <div id='minute-container' className='time-select-container' onScroll={this.onScroll('minute')}>
                    <div className='time-unit-container invis'>
                        <label className='time-unit-label'>0</label>
                    </div>
                    {minutes.map((minute: number, index: number) => (
                        <div id={`minute-${pad(minute)}`} key={index} className='time-unit-container'>
                            <label className='time-unit-label'>{pad(minute)}</label>
                        </div>
                    ))}
                    <div className='time-unit-container invis'>
                        <label className='time-unit-label'>0</label>
                    </div>
                </div>
            </div>
        )
    };

    render = () => {
        const {dateType, pickerValue} = this.props;
        return (
            <div className='timepicker-container'>
                <div className='header-container' style={{backgroundColor: this.getBaseColor()}}>
                    <label className='header-title' style={{color: this.getContrastColor()}}>Kies tijd</label>
                </div>
                <label className={classNames('date-label', dateType != DateType.DATETIME && 'invis')}>{`${pad(
                    pickerValue.day)}-${pad(
                    pickerValue.month + 1)}-${pickerValue.year}`}</label>
                <div className='time-body-container'>
                    <div className='time-column'>
                        <label className='time-col-title'>Uur</label>
                        {this.renderHourSelect()}
                    </div>
                    <div className='time-column'>
                        <label className='time-col-title'>Minuut</label>
                        {this.renderMinuteSelect()}
                    </div>
                </div>
            </div>
        )
    }
}